const MAX_RECENT_EVENTS = 20;
const MAX_MESSAGE_LENGTH = 300;

export type AuthDiagnosticEvent = {
  type: string;
  decision?: string;
  errorCode?: string;
  subError?: string;
  correlationId?: string;
  message?: string;
  attempt?: number;
};

export type AuthDiagnosticRecord = AuthDiagnosticEvent & {
  timestamp: string;
  online?: boolean;
  visibility?: string;
};

export type AuthDiagnosticsSink = (record: AuthDiagnosticRecord) => void;

const recent: AuthDiagnosticRecord[] = [];
let sink: AuthDiagnosticsSink | undefined;

/** Lets a host app forward auth diagnostics (e.g. to Application Insights). Never receives tokens. */
export function setAuthDiagnosticsSink(next: AuthDiagnosticsSink | undefined) {
  sink = next;
}

export function getRecentAuthEvents(): readonly AuthDiagnosticRecord[] {
  return recent;
}

export function describeAuthError(
  error: unknown
): Pick<
  AuthDiagnosticEvent,
  "errorCode" | "subError" | "correlationId" | "message"
> {
  const candidate = (error ?? {}) as Record<string, unknown>;
  const text = (value: unknown) =>
    typeof value === "string" && value ? value : undefined;
  const message =
    text(candidate.errorMessage) ??
    text(candidate.message) ??
    (typeof error === "string" ? error : undefined);

  return {
    errorCode: text(candidate.errorCode) ?? text(candidate.name),
    subError: text(candidate.subError),
    correlationId: text(candidate.correlationId),
    message: message?.slice(0, MAX_MESSAGE_LENGTH),
  };
}

export function reportAuthEvent(event: AuthDiagnosticEvent) {
  const record: AuthDiagnosticRecord = {
    ...event,
    timestamp: new Date().toISOString(),
    online: typeof navigator === "undefined" ? undefined : navigator.onLine,
    visibility:
      typeof document === "undefined" ? undefined : document.visibilityState,
  };

  recent.push(record);
  if (recent.length > MAX_RECENT_EVENTS) recent.shift();

  try {
    if (sink) sink(record);
    else console.warn("[auth]", record);
  } catch {
    // Diagnostics must never break authentication.
  }
}
